/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useQuery } from 'react-query';

import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../../../../../components/AlignCenter/AlignCenter';
import { HUSpin } from '../../../../../../components/HUSpin/HUSpin';
import { OverlayFormWithControls } from '../../../../../../components/OverlayFormWithControls/OverlayFormWithControls';
import { SimpleErrorModal } from '../../../../../../components/SimpleErrorModal/SimpleErrorModal';
import { CLUSTERGW_DEFAULT_DATABASE } from '../../../../../../constants/settings';
import { Namespaces } from '../../../../../../i18n';
import { DatasetImportApiService } from '../../../../../../services/DatasetImportApiService';
import { useCreateEntityOverlayStyles } from '../../../../../../styles/createEntityOverlay';
import { ApiClientError } from '../../../../../../types/api/ApiClientError';
import { ClusterAccessor } from '../../../../../../types/common/Accessors';
import {
  fetchCluster,
  fetchDatabaseListOfCluster,
  fetchDatasets,
  fetchStorageOfCluster,
} from '../../../../../../utils/queryFns';
import { reactToImportClusterDataSetsError, reactToImportDatasets } from '../../../../../../utils/reactions';
import { useObjectState } from '../../../../../../utils/useObjectState';
import { ImportDataSetsClusterForm } from './components/ImportDataSetsClusterForm/ImportDataSetsClusterForm';
import { initialImportDatasetMeta } from './ImportDataSetsCluster.consts';
import { ImportDataSetsClusterValues } from './ImportDataSetsCluster.types';

type ImportDataSetsClusterProps = {
  onSuccess: () => void;
  onCancel: () => void;
  identificationData: ClusterAccessor;
};

type State = {
  isForcedLoading: boolean;
};

type ModalState = {
  errorModal: { isOpen: boolean; error: string };
};

export const ImportDataSetsCluster = memo<ImportDataSetsClusterProps>(props => {
  const classes = useCreateEntityOverlayStyles();
  const { i18n } = useTranslation();
  const { t } = useTranslation([Namespaces.CreateCluster, Namespaces.Cluster]);
  const [state, setState] = useObjectState<State>({ isForcedLoading: false });
  const [modalState, setModalState] = useObjectState<ModalState>({
    errorModal: { isOpen: false, error: '' },
  });

  const { isForcedLoading } = state;
  const { errorModal } = modalState;

  const { onSuccess, onCancel, identificationData } = props;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const dataSetsCategoriesQuery = useQuery([fetchDatasets.key, { language: i18n.language }], fetchDatasets.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const clusterQuery = useQuery([fetchCluster.key, identificationData], fetchCluster.fn);

  const clusterStorageQuery = useQuery([fetchStorageOfCluster.key, identificationData], fetchStorageOfCluster.fn);

  const dataSetsCategories = useMemo(
    () => (dataSetsCategoriesQuery.data?.payload ?? []).filter(i => i.datasets.length > 0),
    [dataSetsCategoriesQuery.data],
  );

  const clusterDatabasesQuery = useQuery(
    [fetchDatabaseListOfCluster.key, identificationData],
    fetchDatabaseListOfCluster.fn,
    {
      refetchOnMount: true,
      cacheTime: 0,
    },
  );

  const onDatabaseDropdownFocus = () => {
    void queryCache.refetchQueries([fetchDatabaseListOfCluster.key, identificationData]);
  };

  const cluster = useMemo(() => clusterQuery.data?.payload, [clusterQuery.data]);

  const clusterStorage = useMemo(() => clusterStorageQuery.data?.payload, [clusterStorageQuery.data]);

  const clusterDatabases = useMemo(() => clusterDatabasesQuery.data?.data.payload ?? [], [clusterDatabasesQuery.data]);

  const isLoading = useMemo(
    () =>
      isForcedLoading ||
      dataSetsCategoriesQuery.status === 'loading' ||
      clusterQuery.status === 'loading' ||
      clusterStorageQuery.status === 'loading' ||
      clusterDatabasesQuery.status === 'loading',
    [
      isForcedLoading,
      clusterDatabasesQuery.status,
      clusterQuery.status,
      clusterStorageQuery.status,
      dataSetsCategoriesQuery.status,
    ],
  );

  const isError =
    !isLoading &&
    (!dataSetsCategories?.length ||
      !clusterDatabases?.length ||
      !cluster ||
      !clusterStorage ||
      !dataSetsCategories.length);

  const initialValues = useMemo<ImportDataSetsClusterValues>(
    () => ({
      selectedDataSetCategoryId: '',
      selectedDataSetIds: [],
      dataSetsMetas: {},
      storageAvailable: clusterStorage?.available ?? 0,
      database:
        clusterDatabases.indexOf(CLUSTERGW_DEFAULT_DATABASE) > -1 ? CLUSTERGW_DEFAULT_DATABASE : clusterDatabases[0],
    }),
    [clusterDatabases, clusterStorage],
  );

  const onFormSubmit = async (formikValues: ImportDataSetsClusterValues) => {
    const { selectedDataSetIds, database, dataSetsMetas } = formikValues;
    if (selectedDataSetIds.length) {
      setIsProcessing(true);
      try {
        await DatasetImportApiService.importClusterDataset({
          ...identificationData,
          data: {
            datasets: selectedDataSetIds.map(datasetId => {
              const meta = dataSetsMetas[datasetId] || initialImportDatasetMeta;
              return {
                database,
                datasetId,
                readonly: meta.readonly,
              };
            }),
          },
        });
        onSuccess();
        void reactToImportDatasets({
          ...identificationData,
        });
        setIsProcessing(false);
      } catch (e) {
        const error = e as ApiClientError;
        const errorMessage = error.response?.data.message;
        setModalState({
          errorModal: {
            isOpen: true,
            error: `${Namespaces.Common}:dataset_import_errors.${errorMessage ?? ''}`,
          },
        });
        if (errorMessage === 'insufficient_space' || errorMessage === 'dataset_already_queued') {
          setState({ isForcedLoading: true });
          await reactToImportClusterDataSetsError(identificationData);
          setState({ isForcedLoading: false });
        }
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t(`${Namespaces.Cluster}:details.datasets.overlay.title`)}</div>
      {isLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.CreateCluster}:labels.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isProcessing} delay={50} disableShrink>
          <Formik<ImportDataSetsClusterValues>
            onSubmit={onFormSubmit}
            validateOnChange
            validateOnBlur
            initialValues={initialValues}
          >
            {formProps => (
              <OverlayFormWithControls onSubmit={formProps.handleSubmit}>
                <ImportDataSetsClusterForm
                  {...formProps}
                  dataSetsCategories={dataSetsCategories}
                  isDatabasesQueryFetching={clusterDatabasesQuery.isFetching}
                  onDatabaseDropdownFocus={onDatabaseDropdownFocus}
                  databases={clusterDatabases}
                  clusterAccessor={identificationData}
                  isDisabledCancelButton={formProps.isValidating || isLoading}
                  isDisabledSubmitButton={
                    formProps.isValidating || isLoading || !formProps.values.selectedDataSetIds.length
                  }
                  onCancel={onCancel}
                />
              </OverlayFormWithControls>
            )}
          </Formik>
        </HUSpin>
      )}
      <SimpleErrorModal
        open={errorModal.isOpen}
        onOk={() =>
          setModalState({
            errorModal: { ...modalState.errorModal, isOpen: false },
          })
        }
        message={errorModal.error}
      />
    </div>
  );
});
